import React, { useState, useEffect, useRef } from 'react';
import { friendlyDeleteError } from '../../utils/deleteError';
import { useMoney } from '../../hooks/useMoney';
import '../../styles/components/FormPage.css';
import Icon from '../Icon';
import { Input as FormInput, Select as FormSelect, Textarea as FormTextarea, Checkbox as FormCheckbox } from '../FormField';
import { CurrencyInput } from '../ui/CurrencyInput';
import RichTextEditor from '../ui/RichTextEditor';
import SidebarPhotoCard from '../ui/SidebarPhotoCard';
import TagMultiSelect from '../ui/TagMultiSelect';
import { uploadImage, resolveMediaUrl } from '../../services/uploadService';
import { addMenuItem, updateMenuItem, archiveMenuItem, loadMenu, MENU_TABS, getCateringVendors, CateringVendor } from '../../services/MenuService';
import { refreshBudgetFromApi } from '../../utils/budgetStorage';
import type { BudgetCategory } from '../../types/ui/Budget';
import { useFormSaveEvent, useFormDeleteEvent, firstMissingField, reportSaveError, useFieldErrors } from './_shared';
import { useNotifications } from '../NotificationProvider';

export const MENU_ITEM_EDIT_KEY = 'anojeje_menu_item_edit_ctx';

export interface MenuItemEditContext {
  tab: string;
  section: string;
  index: number;
  name: string;
  desc: string;
  tags: string[];
  allowance?: number;
  photo_url?: string;
  servings?: number;
  ingredients?: string;
  dietary_flags?: string[];
  caterers?: string[];
  quote_received?: boolean;
  menu_uploaded?: boolean;
  order_confirmed?: boolean;
  invoice_pending?: boolean;
}

interface MenuItemFormPageProps {
  onSave: () => void;
  onCancel: () => void;
  defaultTab?: string;
  editMode?: boolean;
  menuItemId?: string;
  onNameLoad?: (name: string) => void;
}

export const MenuItemFormPage: React.FC<MenuItemFormPageProps> = ({ onSave, onCancel, defaultTab, editMode, menuItemId, onNameLoad }) => {
  const money = useMoney();
  const { showError, showSuccess } = useNotifications();
  const fieldErrors = useFieldErrors();
  const editCtx: MenuItemEditContext | null = editMode
    ? (() => { try { return JSON.parse(sessionStorage.getItem(MENU_ITEM_EDIT_KEY) || 'null'); } catch { return null; } })()
    : null;

  const [form, setForm] = useState({
    tab: editCtx?.tab || defaultTab || 'Traditional',
    section: editCtx?.section || '',
    name: editCtx?.name || '',
    desc: editCtx?.desc || '',
    tags: editCtx?.tags || [] as string[],
    allowance: editCtx?.allowance != null ? String(editCtx.allowance) : '',
    servings: editCtx?.servings != null ? String(editCtx.servings) : '',
    ingredients: editCtx?.ingredients || '',
    dietary_flags: editCtx?.dietary_flags || [] as string[],
    caterers: editCtx?.caterers || [] as string[],
    quote_received: editCtx?.quote_received || false,
    menu_uploaded: editCtx?.menu_uploaded || false,
    order_confirmed: editCtx?.order_confirmed || false,
    invoice_pending: editCtx?.invoice_pending || false,
  });
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(editCtx?.photo_url);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [cateringBudget, setCateringBudget] = useState<BudgetCategory | null>(null);
  const [cateringVendors, setCateringVendors] = useState<CateringVendor[]>([]);

  useEffect(() => {
    if (editCtx?.name) onNameLoad?.(editCtx.name);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshBudgetFromApi().then(cats => {
      const cat = cats.find(c => c.name.toLowerCase().includes('catering')) ?? null;
      setCateringBudget(cat);
    }).catch(() => {});
    getCateringVendors().then(vendors => setCateringVendors(vendors)).catch(() => {});
  }, []);

  useFormSaveEvent(() => handleSave());

  const handleDelete = async () => {
    if (!menuItemId) return;
    try {
      await archiveMenuItem(menuItemId);
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'item', action: 'archive' }));
      return;
    }
    showSuccess('Menu item archived');
    onCancel();
  };

  useFormDeleteEvent(handleDelete);

  const handleSave = async () => {
    const missing = firstMissingField([
      { label: 'Name', value: form.name },
      { label: 'Section', value: form.section },
    ]);
    if (missing) { showError(missing); return; }
    fieldErrors.clear();
    const item = {
      name: form.name.trim(),
      desc: form.desc.trim(),
      tags: form.tags,
      allowance: form.allowance ? Number(form.allowance) : undefined,
      photo_url: photoUrl,
      servings: form.servings ? Number(form.servings) : undefined,
      ingredients: form.ingredients.trim() || undefined,
      dietary_flags: form.dietary_flags.length > 0 ? form.dietary_flags : undefined,
      caterers: form.caterers.length > 0 ? form.caterers : undefined,
      quote_received: form.quote_received || undefined,
      menu_uploaded: form.menu_uploaded || undefined,
      order_confirmed: form.order_confirmed || undefined,
      invoice_pending: form.invoice_pending || undefined,
    };
    if (editCtx) {
      updateMenuItem(editCtx.tab, editCtx.section, editCtx.index, item);
      sessionStorage.removeItem(MENU_ITEM_EDIT_KEY);
    } else {
      try {
        await addMenuItem(form.tab, form.section, item);
      } catch (err) {
        // Don't call onSave — it navigates away, which used to strand the user
        // on the menu believing an item had been added that never was.
        reportSaveError(err, fieldErrors, showError, 'Failed to add menu item.');
        return;
      }
    }
    onSave();
  };

  const DIETARY_OPTIONS = [
    { value: 'vegetarian', label: 'Vegetarian' }, { value: 'vegan', label: 'Vegan' },
    { value: 'halal', label: 'Halal' }, { value: 'gluten-free', label: 'Gluten-free' },
    { value: 'dairy-free', label: 'Dairy-free' }, { value: 'nut-free', label: 'Nut-free' },
    { value: 'spicy', label: 'Spicy' }, { value: 'contains-alcohol', label: 'Contains alcohol' },
  ];

  const [dietarySearch, setDietarySearch] = useState('');
  const [dietaryOpen, setDietaryOpen] = useState(false);
  const dietaryContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (dietaryContainerRef.current && !dietaryContainerRef.current.contains(e.target as Node)) {
        setDietaryOpen(false);
        setDietarySearch('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const toggleDietary = (val: string) =>
    setForm(f => ({
      ...f,
      dietary_flags: f.dietary_flags.includes(val)
        ? f.dietary_flags.filter(d => d !== val)
        : [...f.dietary_flags, val],
    }));

  const handlePhotoFile = async (file: File) => {
    setPhotoUploading(true);
    try {
      const result = await uploadImage(file, { entityType: 'menu_item', entityId: editCtx?.name?.replace(/\s+/g, '-').toLowerCase() || 'new' });
      setPhotoUrl(result.url);
    } catch { /* silently ignore */ } finally { setPhotoUploading(false); }
  };

  const fmtMoney = (n: number) => money.formatCompact(n);

  const statusBadge = (status: string) => {
    const map: Record<string, { label: string; color: string }> = {
      'not-started': { label: 'Not started', color: '#94a3b8' },
      'deposit-paid': { label: 'Deposit paid', color: '#f59e0b' },
      'partially-paid': { label: 'Partial', color: '#f97316' },
      'fully-paid': { label: 'Paid', color: '#22c55e' },
      'overdue': { label: 'Overdue', color: '#ef4444' },
      'pending': { label: 'Pending', color: '#94a3b8' },
    };
    const m = map[status] ?? { label: status, color: '#94a3b8' };
    return (
      <span style={{ /* ds-dynamic: status color from map */ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 99, background: m.color + '22', color: m.color, fontWeight: 600 }}>
        {m.label}
      </span>
    );
  };

  const vendorOptions = cateringVendors.map(v => v.name);

  const sidebar = (
    <>
      <SidebarPhotoCard
        url={photoUrl ? resolveMediaUrl(photoUrl) : undefined}
        alt="Menu item"
        uploading={photoUploading}
        onFileSelect={handlePhotoFile}
        onRemove={() => setPhotoUrl(undefined)}
      />
      {editCtx && (
        <section className="side-section">
          <h4>Section</h4>
          <p className="mip-section-name">{editCtx.section}</p>
          <p className="mip-section-tab">{editCtx.tab}</p>
        </section>
      )}
      <section className="side-section">
        <h4>Caterers</h4>
        <div className="wt-form-group">
          <TagMultiSelect
            values={form.caterers}
            onChange={caterers => setForm(f => ({ ...f, caterers }))}
            options={vendorOptions}
            placeholder="Search vendors…"
            allowCreate={false}
          />
        </div>
        <div className="mip-caterers-divider">
          <p className="mip-status-badges-title">Status Badges</p>
          <FormCheckbox checked={form.quote_received} onChange={e => setForm(f => ({ ...f, quote_received: e.target.checked }))}>Quote received</FormCheckbox>
          <FormCheckbox checked={form.menu_uploaded} onChange={e => setForm(f => ({ ...f, menu_uploaded: e.target.checked }))}>Menu uploaded</FormCheckbox>
          <FormCheckbox checked={form.order_confirmed} onChange={e => setForm(f => ({ ...f, order_confirmed: e.target.checked }))}>Order confirmed</FormCheckbox>
          <FormCheckbox checked={form.invoice_pending} onChange={e => setForm(f => ({ ...f, invoice_pending: e.target.checked }))}>Invoice pending</FormCheckbox>
        </div>
      </section>
    </>
  );

  return (
    <div className="detail-layout">
      <div className="inline-form-page">
        {!editCtx && (
          <>
            <FormSelect label="Event / Tab" value={form.tab}
              onChange={e => setForm({ ...form, tab: e.target.value, section: '' })}
              options={MENU_TABS.map(t => ({ value: t, label: t }))} />
            <>
              <FormInput label="Section / Course" list="menu-sections-list" value={form.section} error={fieldErrors.errorFor('section')}
                onChange={e => { setForm({ ...form, section: e.target.value }); fieldErrors.clear('section'); }}
                placeholder="e.g. Main Course, Dessert, Corner Stands" required />
              <datalist id="menu-sections-list">
                {[...new Set(loadMenu().filter(c => c.tab === form.tab).map(c => c.title))].map(s => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </>
          </>
        )}
        <FormInput label="Item Name" type="text" value={form.name} error={fieldErrors.errorFor('name')}
          onChange={e => { setForm({ ...form, name: e.target.value }); fieldErrors.clear('name'); }} placeholder="e.g. Party Jollof Rice" autoFocus required />
        <RichTextEditor label="Description" rows={3} value={form.desc}
          onChange={v => setForm({ ...form, desc: v })} placeholder="Brief description of the dish..." />
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tags</label>
            <TagMultiSelect values={form.tags}
              onChange={tags => setForm({ ...form, tags })}
              placeholder="Signature, Popular, Heritage" />
          </div>
          <CurrencyInput label="Allowance per Guest" value={form.allowance}
            onChange={e => setForm({ ...form, allowance: e.target.value })} placeholder="e.g. 3,500" />
        </div>
        <div className="form-row">
          <FormInput label="Servings per Pot / Tray" type="number" min="1" value={form.servings}
            onChange={e => setForm({ ...form, servings: e.target.value })} placeholder="e.g. 50" />
        </div>
        <FormTextarea label="Key Ingredients" rows={2} value={form.ingredients}
          onChange={e => setForm({ ...form, ingredients: e.target.value })}
          placeholder="Rice, chicken, tomatoes, seasoning..." />
        <div className="form-section">
          <p className="form-section-title">Dietary Flags</p>
          <div className="tf-tag-input" ref={dietaryContainerRef}
            onClick={() => { setDietaryOpen(true); }}>
            {form.dietary_flags.map(val => {
              const opt = DIETARY_OPTIONS.find(o => o.value === val);
              return (
                <span key={val} className="tf-assignee-tag">
                  {opt?.label ?? val}
                  <button type="button" className="tf-assignee-tag-remove"
                    onMouseDown={e => { e.preventDefault(); toggleDietary(val); }}
                    aria-label={`Remove ${opt?.label ?? val}`}>×</button>
                </span>
              );
            })}
            <input
              type="text"
              className="tf-tag-search"
              value={dietarySearch}
              placeholder={form.dietary_flags.length === 0 ? 'Search dietary flags…' : ''}
              onChange={e => { setDietarySearch(e.target.value); setDietaryOpen(true); }}
              onFocus={() => setDietaryOpen(true)}
              onKeyDown={e => {
                if (e.key === 'Escape') { setDietaryOpen(false); setDietarySearch(''); }
                if (e.key === 'Backspace' && !dietarySearch && form.dietary_flags.length > 0) {
                  toggleDietary(form.dietary_flags[form.dietary_flags.length - 1]);
                }
              }}
            />
          </div>
          {dietaryOpen && (() => {
            const filtered = DIETARY_OPTIONS.filter(o =>
              !form.dietary_flags.includes(o.value) &&
              o.label.toLowerCase().includes(dietarySearch.toLowerCase())
            );
            return filtered.length > 0 ? (
              <div className="tf-tag-dropdown">
                {filtered.map(o => (
                  <button key={o.value} type="button" className="tf-tag-option"
                    onMouseDown={e => {
                      e.preventDefault();
                      toggleDietary(o.value);
                      setDietarySearch('');
                    }}>
                    {o.label}
                  </button>
                ))}
              </div>
            ) : null;
          })()}
        </div>
      </div>
      <aside className="detail-sidebar">{sidebar}</aside>
    </div>
  );
};
